/**
 * @module routes.Sessions.AllCoachesPublicSession._components.useRateNextSlots
 *
 * The soonest bookable time for each of one coach's booking options — the green
 * "NEXT: TOMORROW 1:30 PM" line the coach-page frames (526:35466) put above each
 * session's title.
 *
 * Per option rather than per coach, which `useCoachNextSlots` answers for the
 * cards on the facility page. They cannot share an answer: availability depends on
 * the option's own duration and on the schedule limits attached to that lesson
 * rate, so a coach's next free 30 minutes is not their next free 90.
 *
 * Two round trips per option, and no fewer, for the reason `useFacilityAvailability`
 * documents: `/available_dates` says which days an option has, and only
 * `/coach_profiles/:id/available_times` says when on a given day. Both take
 * `slot_duration` and `lesson_rate_id`, so options cannot be batched into one call
 * even though the endpoints accept several coaches at once.
 *
 * Every option is looked up — nothing is silently dropped — so a coach offering ten
 * options costs twenty requests. They run in parallel and each failure is contained:
 * an option whose lookup fails simply shows no line, and the sessions, prices and
 * buttons on the cards never wait for any of this.
 */
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

import { NextSlot, earliestSlotFrom, nextAvailableDate } from "./coachNextSlot";

/** How far ahead an option is looked at before the line gives up. */
const WINDOW_DAYS = 60;

export interface RateNextSlots {
  /** Lesson rate `id` → its soonest slot, or null once looked up and found empty. */
  nextSlots: Map<number, NextSlot>;
  loading: boolean;
}

interface CoachIds {
  /**
   * The id `/available_dates` matches on — the coach's external id, on public and
   * in-app surfaces alike.
   */
  datesId?: string;
  /**
   * The id `/coach_profiles/:id/available_times` is addressed by. The public route
   * knows a coach by their calendar uuid, the authenticated one by their external
   * id; the two endpoints genuinely disagree, which is why this is two fields.
   */
  timesId?: string;
}

const useRateNextSlots = (
  rates: LessonRateModel[] | undefined,
  { datesId, timesId }: CoachIds,
  isPublicPage = true,
): RateNextSlots => {
  const [nextSlots, setNextSlots] = useState<Map<number, NextSlot>>(new Map());
  const [loading, setLoading] = useState(false);

  // Recompute when the set of options changes, not on every render of the list.
  const rateKey = (rates || []).map((rate) => rate.id).join(",");

  useEffect(() => {
    if (!datesId || !timesId || (rates || []).length === 0) {
      setNextSlots(new Map());
      return undefined;
    }

    let active = true;
    setLoading(true);

    const now = DateTime.local();
    const from = now.toISODate();
    const to = now.plus({ days: WINDOW_DAYS }).toISODate();

    const lookups = (rates || []).map((rate) => {
      const query = {
        slot_duration: rate.durationInMinutes,
        lesson_rate_id: rate.id,
      };

      return schedulingFetchGet(
        "/available_dates",
        { from, to, coach_profile_ids: [datesId], ...query },
        {},
        null,
        isPublicPage,
      )
        .then((profiles: any) => {
          const dates = (profiles || []).flatMap(
            (profile: any) => profile?.dates || [],
          );
          const day = nextAvailableDate(dates, now);
          if (!day) {
            return null;
          }

          return schedulingFetchGet(
            `/coach_profiles/${timesId}/available_times`,
            {
              from: DateTime.fromISO(day).startOf("day").toISO(),
              to: DateTime.fromISO(day).endOf("day").toISO(),
              coach_profile_ids: [timesId],
              ...query,
            },
            {},
            null,
            isPublicPage,
          ).then((raw: any) =>
            earliestSlotFrom(
              (raw?.[0]?.slots || []).map(
                (slot: any) => new AvailableSlotModel(slot),
              ),
              now,
            ),
          );
        })
        .then((slot: NextSlot) => [rate.id, slot] as [number, NextSlot])
        .catch(() => [rate.id, null] as [number, NextSlot]);
    });

    Promise.all(lookups)
      .then((entries) => {
        if (active) {
          setNextSlots(new Map(entries));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateKey, datesId, timesId, isPublicPage]);

  return { nextSlots, loading };
};

export default useRateNextSlots;
